import { useCallback, useEffect, useState } from 'react';
import { UnifiedInstruments } from '#/unified-rfqs/ui/instruments/types';
import * as store from './store';

interface HookArgs {
  readonly prefill?: Partial<store.State>;
  readonly onError: (message: string) => void;
  readonly instruments: UnifiedInstruments;
}
export default function usePrefillNotifier({
  onError,
  prefill,
  ...props
}: HookArgs) {
  const [hasNotified, setHasNotified] = useState(false);

  const checkForInstruments = useCallback(
    (instruments: UnifiedInstruments) => {
      if (prefill?.legs == null || prefill.legs.length === 0) return;
      prefill.legs.forEach((leg) => {
        if (leg.instrumentName == null) return;
        const hasInstrument = doesInstrumentExist(leg, instruments);

        if (!hasInstrument) {
          onError(
            `Previous instrument ${leg.instrumentName} does not exist. Please enter leg values manually.`,
          );
        }
      });
    },
    [prefill, onError],
  );

  const hasLoadedInstruments = store.hasLoadedInstrumentsSelection({
    instruments: props.instruments,
  });

  useEffect(() => {
    if (hasNotified) return;

    if (hasLoadedInstruments) {
      checkForInstruments(props.instruments);
      setHasNotified(true);
    }
  }, [
    checkForInstruments,
    hasLoadedInstruments,
    hasNotified,
    props.instruments,
  ]);
}

function doesInstrumentExist(
  leg: store.State['legs'][number],
  instruments: UnifiedInstruments,
) {
  if (leg.instrumentName == null) return false;
  let foundInst = false;
  if (leg.kind === 'FUTURE') {
    foundInst = instruments.futureInstruments.some(
      (ins) => leg.instrumentName != null && leg.instrumentName === ins.name,
    );
  }
  if (leg.kind === 'OPTION' && !foundInst) {
    foundInst = instruments.optionInstruments.some(
      (ins) => leg.instrumentName != null && leg.instrumentName === ins.name,
    );
  }

  return foundInst;
}
